/**
 * The Charter career bridge's feature (M5 plan C16; the career in the Reach only): once the career (`m2`, with its
 * write hook) and the strategic client (`strategic`, order 170) are provided, creates the bridge
 * (`src/charter/careerBridge.ts`, loaded lazily), binds the faction-request offers into M2's contract boards, and
 * steps it once per frame (no allocation while the career and the Reach events are unchanged). M7: after every worker
 * re-init (a career enter: load, fresh Reach, Continue; `strategic-link` `onWorkerReinit`) the bridge re-applies the
 * story and the standings mirror (`workerReinit`), so the shared story sync's sent-set never hides them from a fresh worker.
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import type { CareerService } from '../../career/flow'
import type { CareerBridge } from '../../charter/careerBridge'
import type { M2Runtime } from '../../space/contracts'

/** Right after the strategic feature (170). */
const ORDER = FEATURE_ORDER.strategic + 1

let bridge: CareerBridge | null = null
let offDeltas: (() => void) | null = null

/** The `m2` service with its dev-command write hook (the career flow's `CareerService`). */
function hasWriteHook(m2: M2Runtime): m2 is CareerService {
  return 'applyDevCommand' in m2 && typeof m2.applyDevCommand === 'function'
}

/** The running bridge (the debug API and the Charter UI), or null outside the Reach. */
export function charterCareerBridge(): CareerBridge | null {
  return bridge
}

const feature: GameFeature = {
  name: 'charterCareer',
  order: ORDER,
  async init(ctx: GameContext): Promise<void> {
    const reach = ctx.reach
    const m2 = ctx.services.maybe('m2')
    const client = ctx.services.maybe('strategic')
    if (reach === null || m2 === undefined || client === undefined || !hasWriteHook(m2)) return
    const [{ createCareerBridge }, { careerAccess }, { bindCharterPorts }, { storySync }, { strategicBoot }] = await Promise.all([
      import('../../charter/careerBridge'),
      import('../../bridge/m2/career'),
      import('../../charter/subsystem'),
      import('../../story-link/service'),
      import('../../strategic-link/service'),
    ])
    const access = careerAccess(m2)
    // M7 S30: the story link's shared re-apply sender, so neither bridge repeats the other's commands.
    const systemOfPoi = (poiId: string): string | undefined => reach.galaxy.systems.find((s) => s.pois.some((p) => p.id === poiId))?.id
    bridge = createCareerBridge({
      access: () => access,
      client,
      data: m2.data,
      reach,
      storySync: storySync(),
      storyWorld: () => ({ systemId: reach.session.layout?.systemId, systemOfPoi }),
      layout: () => reach.session.layout ?? null,
    })
    bridge.install()
    const b = bridge
    // Deltas from advances (worker-side changes) and from Charter command results both reach the Career once.
    const offStanding = client.onStandingDeltas((d) => b.applyStandingDeltas(d))
    // M7 §12.1 logging: guardian kills from advances go to the Career's log.
    const offKills = client.onGuardianKills((k) => b.recordGuardianKills(k))
    // M7: the worker was re-initialized for a career enter; the story re-apply's single owner is this bridge.
    const offReinit = strategicBoot()?.onWorkerReinit((e) => b.workerReinit(e.view ?? undefined)) ?? null
    offDeltas = () => {
      offStanding()
      offKills()
      offReinit?.()
    }
    // C14's M2 ports: piracy (the space event ring), docking policy (station def ids) and smuggling (the hold).
    bindCharterPorts({
      applyStandingDeltas: (d) => b.applyStandingDeltas(d),
      space: m2.space,
      stationDefId: (i) => m2.space.stations.list[i]?.def.id ?? null,
      layout: () => reach.session.layout ?? null,
      tetherstoneT: () => access.career().cargo.wares.tetherstone,
      confiscateTetherstone: (tonnes) => {
        access.apply('charter confiscate tetherstone', (c) => {
          const left = Math.max(0, c.cargo.wares.tetherstone - tonnes)
          return { ...c, cargo: { ...c.cargo, wares: { ...c.cargo.wares, tetherstone: left } } }
        })
      },
    })
  },
  frame(): void {
    bridge?.step()
  },
  dispose(): void {
    offDeltas?.()
    offDeltas = null
    void import('../../charter/subsystem').then(({ bindCharterPorts }) =>
      bindCharterPorts({ applyStandingDeltas: null, space: null, stationDefId: null, layout: null, tetherstoneT: null, confiscateTetherstone: null }),
    )
    bridge?.dispose()
    bridge = null
  },
}

export default feature
